import React, { useState, useEffect, useCallback } from 'react';
import fprService from '../services/fprService';
import sharepointService from '../services/sharepointService';
import clientHelperService from '../services/clientHelperService';
import { buildFprPdfBlob, defaultFprPdfFilename } from './fprPdfExport';

/**
 * Modal that publishes a client's FPR Update Form (PDF) to SharePoint
 * and adds it to the matching knowledgebase client.
 *
 * Mirrors CashflowSharePointModal but generates a PDF client-side
 * via jsPDF instead of pulling an Excel file from the backend.
 */
export default function FprSharePointModal({ client, onClose, onSuccess }) {
  const defaultFilename = defaultFprPdfFilename(client.name);

  const [spClients, setSpClients] = useState([]);
  const [spClient, setSpClient] = useState('');
  const [newSpClientName, setNewSpClientName] = useState('');

  const [kbClients, setKbClients] = useState([]);
  const [kbClientId, setKbClientId] = useState('');
  const [kbClientName, setKbClientName] = useState('');
  const [newKbClientName, setNewKbClientName] = useState('');

  const [folders, setFolders] = useState([]);
  const [folderPath, setFolderPath] = useState('');
  const [loadingFolders, setLoadingFolders] = useState(false);

  const [filename, setFilename] = useState(defaultFilename);
  const [overwrite, setOverwrite] = useState(false);

  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState('');

  // ---------------- data loading ----------------

  const loadFolders = useCallback(async (clientName) => {
    if (!clientName) return '';
    setLoadingFolders(true);
    setFolders([]);
    setFolderPath('');
    try {
      const result = await sharepointService.getClientFolders(clientName);
      let folderList = result.folders || [];

      const rootEntry = folderList.find(f => f.name === '/');
      const rootFullPath = rootEntry?.full_path || clientName;

      let fpDocsFolder = folderList.find(f => f.name?.toLowerCase() === 'fp docs');
      if (!fpDocsFolder) {
        try {
          const created = await sharepointService.createFolder('FP Docs', rootFullPath, clientName, true);
          const createdFolder = created?.folder;
          if (createdFolder?.full_path) {
            fpDocsFolder = {
              id: createdFolder.id,
              name: 'FP Docs',
              full_path: createdFolder.full_path,
              depth: 1,
            };
            folderList = [...folderList, fpDocsFolder];
          }
        } catch (e) {
          console.warn('Could not create FP Docs folder:', e);
        }
      }

      setFolders(folderList.filter(f => f.name !== '/'));

      let resolvedPath = '';
      if (fpDocsFolder) resolvedPath = fpDocsFolder.full_path;
      else if (folderList.length > 0) {
        const first = folderList.find(f => f.name !== '/') || folderList[0];
        resolvedPath = first.full_path;
      }
      setFolderPath(resolvedPath);
      return resolvedPath;
    } catch (e) {
      console.error('Failed to load folders:', e);
      setFolders([]);
      return '';
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clients, spFolders] = await Promise.all([
          clientHelperService.fetchClients().catch(() => []),
          sharepointService.getClients().catch(() => []),
        ]);
        setKbClients(clients);
        setSpClients(spFolders);

        const normalize = s => (s || '').toLowerCase().replace(/[^a-z]/g, '');
        // FPR client names are stored as "First Last"; KB/SP names are usually
        // "Last, First". Derive both name parts to fuel fuzzy matching.
        const rawName = (client.name || '').trim();
        const parts = rawName.includes(',')
          ? rawName.split(',').map(s => s.trim())
          : rawName.split(/\s+/).reverse();
        const last = parts[0] || '';
        const first = parts[1] || '';
        const nLast = normalize(last);
        const nFirst = normalize(first);

        // Score a candidate string against the client name. Higher = better.
        const scoreCandidate = (candidate) => {
          const n = normalize(candidate);
          if (!n) return 0;
          const exactJoin = nLast + nFirst;
          const exactReverse = nFirst + nLast;
          if (n === exactJoin || n === exactReverse) return 100;
          // last + first-prefix (handles "Greg" vs "Gregory")
          if (nLast && nFirst && n.startsWith(nLast) && n.slice(nLast.length).startsWith(nFirst.slice(0, 3))) return 90;
          if (nLast && nFirst && n.startsWith(nFirst) && n.slice(nFirst.length).startsWith(nLast.slice(0, 3))) return 85;
          if (nLast && n.includes(nLast) && nLast.length >= 3) return 60;
          return 0;
        };

        // Preselect KB client by explicit ID or name match.
        let matchedKbId = '';
        let matchedKbName = '';
        if (client.knowledgebase_client_id) {
          const m = clients.find(c => c.client_id === client.knowledgebase_client_id);
          if (m) { matchedKbId = m.client_id; matchedKbName = m.client_name; }
        }
        if (!matchedKbId) {
          let best = null;
          let bestScore = 0;
          clients.forEach(c => {
            const fields = [c.client_name, ...(c.nicknames || [])];
            const s = Math.max(...fields.map(scoreCandidate));
            if (s > bestScore) { best = c; bestScore = s; }
          });
          if (best && bestScore >= 60) {
            matchedKbId = best.client_id;
            matchedKbName = best.client_name;
          }
        }
        if (matchedKbId) { setKbClientId(matchedKbId); setKbClientName(matchedKbName); }

        // Preselect SP folder using KB name (preferred) or client name.
        const candidatesForSp = [matchedKbName, rawName].filter(Boolean);
        let bestSp = null;
        let bestSpScore = 0;
        spFolders.forEach(folder => {
          const cleaned = folder.replace(/_REBELLIONAIRE/gi, '');
          const s = Math.max(
            scoreCandidate(cleaned),
            ...candidatesForSp.map(cand => {
              const nf = normalize(cleaned);
              const nc = normalize(cand);
              if (!nf || !nc) return 0;
              if (nf === nc) return 100;
              if (nf.includes(nc) || nc.includes(nf)) return 70;
              return 0;
            }),
          );
          if (s > bestSpScore) { bestSp = folder; bestSpScore = s; }
        });
        if (bestSp && bestSpScore >= 60) setSpClient(bestSp);
      } catch (e) {
        console.error('Failed to load client data:', e);
      }
    };
    loadData();
  }, [client.knowledgebase_client_id, client.name]);

  useEffect(() => {
    if (spClient && spClient !== '__new__') loadFolders(spClient);
    else { setFolders([]); setFolderPath(''); }
  }, [spClient, loadFolders]);

  // ---------------- handlers ----------------

  const handleSpClientChange = (value) => {
    setSpClient(value);
    setNewSpClientName('');
    if (value && value !== '__new__') {
      const normalized = value.replace(/_REBELLIONAIRE/gi, '').toLowerCase().trim();
      let match = kbClients.find(c => c.client_name.toLowerCase().trim() === normalized);
      if (!match) match = kbClients.find(c =>
        c.client_name.toLowerCase().includes(normalized) ||
        normalized.includes(c.client_name.toLowerCase())
      );
      if (!match) match = kbClients.find(c =>
        c.nicknames?.some(n =>
          n.toLowerCase().trim() === normalized ||
          n.toLowerCase().includes(normalized) ||
          normalized.includes(n.toLowerCase())
        )
      );
      setKbClientId(match?.client_id || '');
      setKbClientName(match?.client_name || '');
      setNewKbClientName('');
    } else if (!value) {
      setKbClientId('');
      setKbClientName('');
      setNewKbClientName('');
    }
  };

  const handleKbClientChange = (e) => {
    const selectedId = e.target.value;
    if (selectedId === '__new__') {
      setKbClientId('__new__');
      setKbClientName('');
      const suggestion = client.name.includes(',')
        ? client.name.trim()
        : client.name.trim().split(/\s+/).reverse().join(', ');
      setNewKbClientName(suggestion);
      return;
    }
    const selected = kbClients.find(c => c.client_id === selectedId);
    setKbClientId(selectedId);
    setKbClientName(selected?.client_name || '');
    setNewKbClientName('');
  };

  const handlePublish = async () => {
    const resolvedSpFolder = spClient === '__new__' ? newSpClientName.trim() : spClient;
    let resolvedKbClientId = kbClientId;
    let resolvedKbClientName = kbClientName;

    if (!resolvedSpFolder) { setError('Please select or create a SharePoint folder.'); return; }
    if (kbClientId === '__new__') {
      const kbName = newKbClientName.trim();
      if (!kbName) { setError('Please enter a name for the new knowledgebase client.'); return; }
      resolvedKbClientName = kbName;
    } else if (!kbClientId) {
      setError('Please select or create a knowledgebase client.');
      return;
    }
    if (!folderPath && spClient !== '__new__') {
      setError('Please select a SharePoint destination folder.');
      return;
    }
    if (!filename.trim()) { setError('Filename cannot be empty.'); return; }

    setLoading(true);
    setError('');

    let resolvedFolderPath = folderPath;

    try {
      if (spClient === '__new__') {
        setStatusMsg('Creating SharePoint client folder…');
        try {
          await sharepointService.createClient(resolvedSpFolder);
        } catch (spErr) {
          console.warn('SharePoint folder creation failed (may already exist):', spErr);
        }
        resolvedFolderPath = await loadFolders(resolvedSpFolder);
        if (!resolvedFolderPath) {
          throw new Error('SharePoint folder could not be located. Please check SharePoint and try again.');
        }
      }

      if (kbClientId === '__new__') {
        setStatusMsg('Creating knowledgebase client…');
        const kbResult = await clientHelperService.createKbClient({ client_name: resolvedKbClientName });
        resolvedKbClientId = kbResult.client_id;
      }

      const effectiveFolderPath = resolvedFolderPath;
      if (!effectiveFolderPath) {
        throw new Error('No destination folder available. Please select a folder and try again.');
      }

      // Step 1 — Fetch latest form data + generate PDF client-side.
      setStatusMsg('Generating PDF…');
      const detail = await fprService.getClientDetail(client.client_id);
      const formData = detail?.form?.form_data || {};
      const pdfBlob = buildFprPdfBlob(formData, {
        clientName: client.name,
        clientEmail: client.email,
      });
      const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });

      // Step 2 — Upload to SharePoint service staging bucket.
      setStatusMsg('Uploading to staging…');
      const uploadResult = await sharepointService.uploadFile(
        pdfFile,
        resolvedSpFolder,
        resolvedKbClientId,
        null,
        { skipAnalysis: true, skipRedaction: true },
      );
      const fileId = uploadResult.fileId;
      if (!fileId) throw new Error('Upload failed — no fileId returned.');

      // Step 3 — Set the accepted (display) filename.
      setStatusMsg('Setting filename…');
      await sharepointService.updateFileName(fileId, filename, resolvedSpFolder);

      // Step 4 — Approve & push to SharePoint + knowledgebase.
      setStatusMsg('Publishing to SharePoint and knowledgebase…');
      const result = await sharepointService.approveFileAndSummary(
        fileId,
        resolvedSpFolder,
        resolvedKbClientId,
        '',
        effectiveFolderPath,
        overwrite,
        'Mixed',
        'Mixed',
        'NONE',
      );

      if (result.success) {
        onSuccess(result);
      } else if (result.error === 'FILE_EXISTS') {
        setError(
          'A file with this name already exists in the selected folder. ' +
          'Enable "Overwrite existing file" to replace it.',
        );
      } else {
        setError(result.error || result.message || 'Publish failed. Please try again.');
      }
    } catch (e) {
      console.error('Publish error:', e);
      setError(e.response?.data?.detail || e.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
      setStatusMsg('');
    }
  };

  // ---------------- render ----------------

  const resolvedSpFolderCheck = spClient === '__new__' ? newSpClientName.trim() : spClient;
  const canSubmit = !loading &&
    resolvedSpFolderCheck &&
    (kbClientId === '__new__' ? newKbClientName.trim() : kbClientId) &&
    (spClient === '__new__' || (folderPath && filename.trim()));

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">

          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-cloud-upload me-2"></i>
              Publish to SharePoint — <span className="text-muted">{client.name}</span>
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            />
          </div>

          <div className="modal-body">
            {error && (
              <div className="alert alert-danger d-flex align-items-start gap-2">
                <i className="bi bi-exclamation-triangle-fill mt-1 flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-semibold">
                SharePoint Folder <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={spClient}
                onChange={(e) => handleSpClientChange(e.target.value)}
                disabled={loading}
              >
                <option value="">— Select SharePoint folder —</option>
                <option value="__new__">+ Create new SharePoint folder</option>
                {[...spClients].sort((a, b) => a.localeCompare(b)).map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {spClient === '__new__' && (
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder='Folder name, e.g. "Schmitz, Greg"'
                  value={newSpClientName}
                  onChange={(e) => setNewSpClientName(e.target.value)}
                  disabled={loading}
                />
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Knowledgebase Client <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={kbClientId}
                onChange={handleKbClientChange}
                disabled={loading}
              >
                <option value="">— Select a client —</option>
                <option value="__new__">+ Create new knowledgebase client</option>
                {[...kbClients].sort((a, b) => a.client_name.localeCompare(b.client_name)).map(c => (
                  <option key={c.client_id} value={c.client_id}>
                    {c.client_name}
                  </option>
                ))}
              </select>
              {kbClientId === '__new__' && (
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder='Name, e.g. "Schmitz, Greg"'
                  value={newKbClientName}
                  onChange={(e) => setNewKbClientName(e.target.value)}
                  disabled={loading}
                />
              )}
              <div className="form-text">
                Select the matching knowledgebase client to link this file to their record.
              </div>
            </div>

            {spClient !== '__new__' && (
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Destination Folder <span className="text-danger">*</span>
              </label>
              {loadingFolders ? (
                <div className="text-muted small py-2">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Loading SharePoint folders…
                </div>
              ) : (
                <select
                  className="form-select"
                  value={folderPath}
                  onChange={(e) => setFolderPath(e.target.value)}
                  disabled={loading || !spClient}
                >
                  <option value="">— Select a folder —</option>
                  {folders.map(f => (
                    <option key={f.full_path} value={f.full_path}>
                      {f.name}
                    </option>
                  ))}
                </select>
              )}
              {folderPath && (
                <div className="form-text text-muted font-monospace">
                  <i className="bi bi-folder2-open me-1"></i>
                  {folderPath}
                </div>
              )}
              {spClient && !loadingFolders && folders.length === 0 && (
                <div className="form-text text-warning">
                  No folders found for this client in SharePoint.
                </div>
              )}
            </div>
            )}
            {spClient === '__new__' && (
              <div className="alert alert-info d-flex align-items-start gap-2 mb-3">
                <i className="bi bi-info-circle-fill mt-1 flex-shrink-0"></i>
                <span>
                  A new SharePoint client folder and <strong>FP Docs</strong> subfolder will be created automatically on publish.
                </span>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-semibold">File Name</label>
              <input
                type="text"
                className="form-control font-monospace"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                disabled={loading}
              />
              <div className="form-text">
                Standard format: <code>FPR-Update Form-[Last Name]-[Year].pdf</code>
              </div>
            </div>

            <div className="mb-3 form-check">
              <input
                id="fpr-overwrite"
                type="checkbox"
                className="form-check-input"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="fpr-overwrite">
                Overwrite existing file if it already exists in SharePoint
              </label>
            </div>

            {statusMsg && (
              <div className="alert alert-info d-flex align-items-center gap-2 mb-0">
                <span className="spinner-border spinner-border-sm flex-shrink-0"></span>
                <span>{statusMsg}</span>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handlePublish}
              disabled={!canSubmit}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Publishing…
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-1"></i>
                  Publish
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
